<template>
  <div class="task-runs" v-loading="loading">
    <section class="page-hero">
      <div>
        <el-tag size="small" type="info" effect="plain">父任务下的子模块明细</el-tag>
        <h1>运行记录</h1>
        <p>父任务负责一次运行与一次飞书汇总；本页保留公告、新闻、董秘问答和系统自检的真实子模块明细。</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="loading" @click="loadRuns(false)">刷新记录</el-button>
    </section>

    <el-alert
      class="scope-alert"
      type="success"
      :closable="false"
      show-icon
      title="父任务运行回执已接入：同一时间出现多个子模块记录属于一次父任务内部展开，不代表重复执行或重复发送飞书。"
    />

    <el-card class="panel contract-panel" shadow="never">
      <div class="contract-row">
        <div>
          <strong>子模块运行记录只读合同</strong>
          <p>{{ contractVersion || '正在读取合同版本' }}</p>
        </div>
        <div class="contract-tags">
          <el-tag size="small" :type="contractReady ? 'success' : 'danger'">
            {{ contractReady ? '合同校验通过' : '合同未就绪' }}
          </el-tag>
          <el-tag size="small" type="success" effect="plain">父任务聚合已接通</el-tag>
          <el-tag size="small" :type="sideEffectsClean ? 'success' : 'danger'">
            {{ sideEffectsClean ? '读取无写入副作用' : '副作用校验失败' }}
          </el-tag>
        </div>
      </div>
    </el-card>

    <el-card class="panel filter-panel" shadow="never">
      <div class="filter-row">
        <el-select v-model="filters.group" placeholder="父任务" clearable size="small">
          <el-option label="市场事件监控" value="market_event_watch" />
          <el-option label="个股事件监控" value="stock_event_watch" />
          <el-option label="持仓上下文监控" value="position_context_watch" />
          <el-option label="系统健康检查" value="system_health_check" />
        </el-select>
        <el-select v-model="filters.status" placeholder="状态" clearable size="small">
          <el-option label="成功" value="success" />
          <el-option label="失败" value="failed" />
          <el-option label="执行中" value="running" />
          <el-option label="等待中" value="pending" />
        </el-select>
        <el-input
          v-model.trim="filters.batchLogId"
          size="small"
          clearable
          placeholder="完整批次编号（精确查询）"
          @keyup.enter.native="applyFilters"
        />
        <el-button size="small" type="primary" icon="el-icon-search" @click="applyFilters">查询</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="clearFilters">重置</el-button>
      </div>
      <p class="filter-note">市场父任务仅包含系统性市场新闻；公司公告归入个股父任务。子模块不单独发送飞书。</p>
    </el-card>

    <el-card class="panel" shadow="never">
      <div slot="header" class="panel-head">
        <div>
          <strong>子模块运行记录</strong>
          <p>每行是一个内部模块；父任务编号、总体状态和飞书汇总请返回“任务监控”查看。</p>
        </div>
        <div class="page-size-control">
          <span>每页</span>
          <el-select v-model="pageSize" size="mini" @change="changePageSize">
            <el-option :value="20" label="20" />
            <el-option :value="50" label="50" />
            <el-option :value="100" label="100" />
          </el-select>
          <span>条</span>
        </div>
      </div>

      <el-table :data="runs" size="small" stripe empty-text="暂无子模块运行记录" @row-click="showDetail">
        <el-table-column label="完整运行编号" min-width="305">
          <template slot-scope="scope">
            <div class="full-id-cell">
              <span>{{ scope.row.run_id || '完整编号暂不可用' }}</span>
              <el-button v-if="scope.row.run_id" type="text" size="mini" @click.stop="copyText(scope.row.run_id, '运行编号')">复制</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="完整批次编号" min-width="315">
          <template slot-scope="scope">
            <div class="full-id-cell">
              <span>{{ batchLogId(scope.row) }}</span>
              <el-button v-if="batchLogId(scope.row) !== '批次编号暂不可用'" type="text" size="mini" @click.stop="copyText(batchLogId(scope.row), '批次编号')">复制</el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="父任务" min-width="150">
          <template slot-scope="scope">
            <el-tag size="small" effect="plain" :type="groupTag(parentGroupId(scope.row))">
              {{ groupLabelById(parentGroupId(scope.row)) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="内部模块" width="110">
          <template slot-scope="scope">{{ moduleLabel(scope.row.task_type) }}</template>
        </el-table-column>
        <el-table-column label="运行时间" width="165">
          <template slot-scope="scope">{{ displayRunTime(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="130">
          <template slot-scope="scope">
            <el-tag size="mini" :type="runStatusType(scope.row)">{{ runStatusText(scope.row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="新增条目" width="85">
          <template slot-scope="scope">{{ countText(scope.row.new_items_count) }}</template>
        </el-table-column>
        <el-table-column label="去重条目" width="85">
          <template slot-scope="scope">{{ countText(scope.row.deduped_count) }}</template>
        </el-table-column>
        <el-table-column label="通知方式" width="105">
          <template slot-scope="scope">
            <el-tag v-if="isParentAggregated(scope.row)" size="mini" type="success" effect="plain">父级汇总</el-tag>
            <span v-else>{{ legacyNotificationText(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="监控范围" min-width="170">
          <template slot-scope="scope">{{ runScopeText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="结果摘要" min-width="270">
          <template slot-scope="scope">{{ runResultText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click.stop="showDetail(scope.row)">查看详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>{{ pageSummary }}</span>
        <div>
          <el-button size="small" :disabled="offset === 0 || loading" @click="previousPage">上一页</el-button>
          <el-button size="small" type="primary" :disabled="!hasMore || loading" @click="nextPage">下一页</el-button>
        </div>
      </div>
    </el-card>

    <el-drawer
      title="子模块运行详情"
      :visible.sync="showDetailDrawer"
      direction="rtl"
      size="760px"
      :append-to-body="true"
    >
      <div class="detail-body" v-loading="detailLoading">
        <el-alert
          type="info"
          :closable="false"
          show-icon
          title="此详情是父任务内部的单个模块记录；父任务总体状态和一次飞书汇总请在任务监控父运行详情中查看。"
        />

        <el-descriptions v-if="currentRun" class="detail-descriptions" :column="1" border size="small">
          <el-descriptions-item label="记录口径">父任务内部子模块</el-descriptions-item>
          <el-descriptions-item label="完整运行编号">
            <div class="detail-id">
              <span>{{ currentRun.run_id || '完整编号暂不可用' }}</span>
              <el-button v-if="currentRun.run_id" type="text" size="mini" @click="copyText(currentRun.run_id, '运行编号')">复制</el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="完整任务编号">
            <div class="detail-id">
              <span>{{ currentRun.task_id || '完整编号暂不可用' }}</span>
              <el-button v-if="currentRun.task_id" type="text" size="mini" @click="copyText(currentRun.task_id, '任务编号')">复制</el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="完整批次编号">
            <div class="detail-id">
              <span>{{ batchLogId(currentRun) }}</span>
              <el-button v-if="batchLogId(currentRun) !== '批次编号暂不可用'" type="text" size="mini" @click="copyText(batchLogId(currentRun), '批次编号')">复制</el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="所属父任务">{{ groupLabelById(parentGroupId(currentRun)) }}</el-descriptions-item>
          <el-descriptions-item label="内部模块">{{ moduleLabel(currentRun.task_type) }}</el-descriptions-item>
          <el-descriptions-item label="父任务回执">{{ parentRunId(currentRun) || '请在任务监控父运行详情中查看' }}</el-descriptions-item>
          <el-descriptions-item label="飞书通知">{{ isParentAggregated(currentRun) ? '由父任务汇总发送，子模块不单独发送' : legacyNotificationText(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="运行状态">
            <el-tag size="mini" :type="runStatusType(currentRun)">{{ runStatusText(currentRun) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="数据状态">{{ dataStatusText(currentRun.data_status || payloadValue(currentRun, 'data_status')) }}</el-descriptions-item>
          <el-descriptions-item v-if="failureStage(currentRun)" label="失败阶段">{{ failureStage(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="新增 / 去重">{{ countText(currentRun.new_items_count) }} / {{ countText(currentRun.deduped_count) }}</el-descriptions-item>
          <el-descriptions-item label="监控范围">{{ runScopeText(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="匹配股票">{{ matchedSymbolsText(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="运行时间">{{ displayRunTime(currentRun) }}</el-descriptions-item>
          <el-descriptions-item label="完成时间">{{ displayTime(currentRun.completed_at) }}</el-descriptions-item>
          <el-descriptions-item label="结果摘要">{{ runResultText(currentRun) }}</el-descriptions-item>
          <el-descriptions-item v-if="currentRun.error_message" label="错误信息">
            <span class="danger-text">{{ currentRun.error_message }}</span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import BaseScheduledTaskRuns from './ScheduledTaskRuns.vue'

const TIMEOUT_DATA_STATUSES = ['parent_terminal_timeout']
const CONTRACT_DATA_STATUSES = ['handler_contract_violation', 'handler_failure_persistence']

export default {
  name: 'ScheduledTaskRunsParentRuntime',
  extends: BaseScheduledTaskRuns,
  methods: {
    payloadObject(row) {
      return row && row.machine_payload && typeof row.machine_payload === 'object'
        ? row.machine_payload
        : {}
    },
    parentDispatch(row) {
      const payload = this.payloadObject(row)
      return payload.parent_dispatch && typeof payload.parent_dispatch === 'object'
        ? payload.parent_dispatch
        : {}
    },
    failureCapture(row) {
      const payload = this.payloadObject(row)
      return payload.failure_capture && typeof payload.failure_capture === 'object'
        ? payload.failure_capture
        : {}
    },
    parentRunId(row) {
      const payload = this.payloadObject(row)
      return row && row.parent_run_id || payload.parent_run_id || this.parentDispatch(row).parent_run_id || ''
    },
    parentGroupId(row) {
      if (row && row.parent_group_id) return row.parent_group_id
      const dispatch = this.parentDispatch(row)
      if (dispatch.group_id) return dispatch.group_id
      const taskType = row && row.task_type
      if (taskType === 'news_watch') return 'market_event_watch'
      if (['announcement_watch', 'stock_news_watch', 'irm_qa_watch'].includes(taskType)) return 'stock_event_watch'
      if (taskType === 'system_health_check') return 'system_health_check'
      return 'unmapped'
    },
    moduleLabel(taskType) {
      if (taskType === 'announcement_watch') return '公司公告'
      return BaseScheduledTaskRuns.methods.moduleLabel.call(this, taskType)
    },
    runScopeText(row) {
      const groupId = this.parentGroupId(row)
      if (groupId === 'market_event_watch') return '全市场重大事件（系统管理）'
      if (groupId === 'stock_event_watch') return '第一本账全范围'
      return BaseScheduledTaskRuns.methods.runScopeText.call(this, row)
    },
    isParentAggregated(row) {
      const dispatch = this.parentDispatch(row)
      return Boolean(this.parentRunId(row) || dispatch.child_notification_suppressed === true)
    },
    legacyNotificationText(row) {
      const count = Number(row && row.notified_count)
      return Number.isFinite(count) && count > 0 ? `历史涉及 ${count} 条` : '历史记录'
    },
    runDataStatus(row) {
      return String(row && row.data_status || this.payloadValue(row, 'data_status') || '').toLowerCase()
    },
    runStatusText(row) {
      const dataStatus = this.runDataStatus(row)
      if (TIMEOUT_DATA_STATUSES.includes(dataStatus)) return '父任务超时收尾'
      if (CONTRACT_DATA_STATUSES.includes(dataStatus)) return '子模块异常收尾'
      if (dataStatus === 'stale_running') return '陈旧运行收尾'
      return BaseScheduledTaskRuns.methods.statusText.call(this, row && row.status)
    },
    runStatusType(row) {
      const dataStatus = this.runDataStatus(row)
      if (TIMEOUT_DATA_STATUSES.includes(dataStatus) || CONTRACT_DATA_STATUSES.includes(dataStatus) || dataStatus === 'stale_running') return 'danger'
      return BaseScheduledTaskRuns.methods.statusType.call(this, row && row.status)
    },
    failureStage(row) {
      const capture = this.failureCapture(row)
      const map = {
        parent_terminal_wait_timeout: '父任务终态等待超时',
        synchronous_handler_returned_non_terminal: '同步子处理器返回时未形成终态',
        handler_exception_failed_status_write: '子处理器异常失败状态写入',
        fresh_session_failed_status_recovery: '使用独立会话恢复失败收尾'
      }
      return map[capture.stage] || capture.stage || ''
    },
    runResultText(row) {
      const dataStatus = this.runDataStatus(row)
      if (TIMEOUT_DATA_STATUSES.includes(dataStatus)) {
        return '父任务等待超时，子模块已按精确运行编号失败收尾；通知由父任务汇总'
      }
      if (CONTRACT_DATA_STATUSES.includes(dataStatus)) {
        return '子处理器未形成可靠终态，已失败收尾；通知由父任务汇总'
      }
      if (dataStatus === 'stale_running') {
        return '历史陈旧运行已收尾；不代表本轮仍在执行'
      }
      const base = BaseScheduledTaskRuns.methods.runResultText.call(this, row)
      return this.isParentAggregated(row) ? `${base}；通知由父任务汇总` : base
    }
  }
}
</script>

<style scoped>
.task-runs { max-width: 1560px; margin: 0 auto; }
.page-hero { min-height: 104px; padding: 22px 26px; border-radius: 18px; background: #f7faff; border: 1px solid #dbe7f5; display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.page-hero h1 { margin: 10px 0 6px; color: #0f172a; font-size: 28px; }
.page-hero p { margin: 0; color: #64748b; }
.scope-alert { margin-bottom: 14px; }
.panel { border-radius: 16px; margin-bottom: 14px; }
.contract-panel { background: #fbfdff; }
.contract-row { display: flex; justify-content: space-between; align-items: center; gap: 18px; }
.contract-row strong { color: #0f172a; }
.contract-row p { margin: 5px 0 0; color: #64748b; font-family: Consolas, Monaco, monospace; font-size: 12px; }
.contract-tags { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.filter-row { display: grid; grid-template-columns: 190px 140px minmax(330px, 1fr) 88px 88px; gap: 10px; align-items: center; }
.filter-note { margin: 10px 0 0; color: #7f8ea3; font-size: 12px; }
.panel-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.panel-head strong { color: #0f172a; }
.panel-head p { margin: 4px 0 0; color: #7f8ea3; font-size: 12px; }
.page-size-control { display: flex; align-items: center; gap: 7px; color: #64748b; font-size: 12px; }
.page-size-control .el-select { width: 72px; }
.full-id-cell, .detail-id { display: flex; align-items: center; justify-content: space-between; gap: 8px; color: #334155; font-family: Consolas, Monaco, monospace; }
.full-id-cell { min-width: max-content; white-space: nowrap; }
.full-id-cell > span { white-space: nowrap; word-break: normal; }
.detail-id { word-break: break-all; }
.pagination-row { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding-top: 16px; color: #64748b; font-size: 13px; }
.detail-body { padding: 0 22px 28px; }
.detail-descriptions { margin-top: 16px; }
.danger-text { color: #ef4444; }
@media (max-width: 1000px) {
  .filter-row { grid-template-columns: 1fr; }
  .page-hero, .contract-row, .panel-head, .pagination-row { flex-direction: column; align-items: flex-start; }
  .contract-tags { justify-content: flex-start; }
}
</style>
